import { Checkbox, Form, Input, InputNumber, Select } from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { api, endpoint } from "../../../../api";
import { userRole } from "../../../../constant/userRole";
import { tabProps } from "../../../../utils/globalPropsTypes";

// business, device, location, yAxis

function DataTab({ form, editItem, setEditItem, data: dashboardData = {}, type = "dashboard" }) {
  const { component, counterDataType } = editItem;
  const userDetails = useSelector((state) => state?.user?.userDetails);

  // state
  const [businessList, setBusinessList] = useState([]);
  const [deviceList, setDeviceList] = useState([]);
  const [parameterList, setParameterList] = useState([]);
  const [siteLocationList, setSiteLocationList] = useState([]);
  // loading
  const [isSiteLoading, setIsSiteLoading] = useState(false);
  const [isParameterLoading, setIsParameterLoading] = useState(false);
  const [isDeviceLoading, setIsDeviceLoading] = useState(false);

  // fetch
  const getBusiness = async () => {
    const params = {
      unknown: true,
    };
    if (dashboardData.business?.length) {
      params.business = dashboardData.business;
    }
    try {
      const data = await api.get(endpoint.business, { params });

      setBusinessList(data?.data);
      if (data?.data?.length === 1) {
        if (component === "comboBar") {
          const currentLine = form.getFieldValue(["comboLocations", "line"]) || [{}];
          const currentBar = form.getFieldValue(["comboLocations", "bar"]) || [{}];

          const updatedLine = currentLine.map((item, index) =>
            index === 0 ? { ...item, business: [data?.data[0]._id] } : item,
          );

          const updatedBar = currentBar.map((item, index) =>
            index === 0 ? { ...item, business: [data?.data[0]._id] } : item,
          );

          form.setFieldsValue({
            comboLocations: {
              line: updatedLine,
              bar: updatedBar,
            },
          });
        } else if (component === "alarmNotification") {
          form.setFieldsValue({
            business: data?.data[0]._id,
          });
          setEditItem((pre) => ({ ...pre, business: data?.data[0]._id }));
        } else {
          form.setFieldsValue({
            locations: [{ ...form.getFieldValue(["locations", 0]), business: data?.data[0]._id }],
          });
        }
        getSiteLocations(data?.data[0]._id);
      }
    } catch {
      setBusinessList([]);
      form.setFieldsValue({ locations: [{ business: undefined }] });
    }
  };

  const getSiteLocations = async (business) => {
    if (!business || (Array.isArray(business) && !business.length)) return setSiteLocationList([]);
    setIsSiteLoading(true);

    try {
      const params = { business, limit: 10000 };
      if (dashboardData.site?.length) {
        params.sites = dashboardData.site;
      }
      // console.log({ params, dashboardData });
      const data = await api.get(`/site-location/my-site`, { params });
      if (data) {
        setSiteLocationList(data?.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)));
      } else {
        setSiteLocationList([]);
      }
    } catch {
      setSiteLocationList([]);
    } finally {
      setIsSiteLoading(false);
    }
  };

  // get device
  const getDevice = async (site) => {
    if (!site.length) return setDeviceList([]);
    setIsDeviceLoading(true);
    try {
      setDeviceList([]);
      const { results } = await api.get(endpoint.get_devices, {
        params: {
          site,
          limit: 100000,
          page: 1,
        },
      });

      if (results) {
        setDeviceList(results);
      }
    } catch {
      setDeviceList([]);
    } finally {
      setIsDeviceLoading(false);
    }
  };

  // get device parameter
  const getDeviceParameters = async (deviceId) => {
    if (!deviceId?.length) return setParameterList([]);
    setIsParameterLoading(true);
    try {
      setParameterList([]);
      const { results } = await api.get(endpoint.get_device_parameter, {
        params: {
          deviceId,
          limit: 1000,
          page: 1,
          sortBy: "name",
          orderBy: "ASC",
          type: "formula",
        },
      });
      setParameterList(results);
    } catch {
      setParameterList([]);
    } finally {
      setIsParameterLoading(false);
    }
  };

  // effects
  useEffect(() => {
    if ((Array.isArray(dashboardData?.business) && dashboardData.business?.length) || type === "report") {
      getBusiness();
    }
  }, [type]);

  useEffect(() => {
    if (component === "comboBar") {
      const businessFieldLine = form.getFieldValue(["comboLocations", "line", 0, "business"]);
      if (businessFieldLine && businessFieldLine.length > 0) {
        getSiteLocations(businessFieldLine);
      }
      const businessFieldBar = form.getFieldValue(["comboLocations", "bar", 0, "business"]);
      if (businessFieldBar && businessFieldBar.length > 0) {
        getSiteLocations(businessFieldBar);
      }
    } else {
      const businessField = form.getFieldValue(["locations", 0, "business"]);
      if (businessField && businessField.length > 0) {
        getSiteLocations(businessField);
      }
    }
  }, [form]);

  useEffect(() => {
    if (component === "comboBar") {
      const locationFieldLine = form.getFieldValue(["comboLocations", "line", 0, "site"]);
      if (locationFieldLine && locationFieldLine.length > 0) {
        getDevice(locationFieldLine);
      }
      const locationFieldBar = form.getFieldValue(["comboLocations", "bar", 0, "site"]);
      if (locationFieldBar && locationFieldBar.length > 0) {
        getDevice(locationFieldBar);
      }
    } else {
      const locationField = form.getFieldValue(["locations", 0, "site"]);
      if (locationField && locationField.length > 0) {
        getDevice(locationField);
      }
    }
  }, [form]);

  useEffect(() => {
    if (component === "comboBar") {
      const deviceFieldLine = form.getFieldValue(["comboLocations", "line", 0, "device"]);
      if (deviceFieldLine && deviceFieldLine.length > 0) {
        getDeviceParameters(deviceFieldLine);
      }
      const deviceFieldBar = form.getFieldValue(["comboLocations", "bar", 0, "device"]);
      if (deviceFieldBar && deviceFieldBar.length > 0) {
        getDeviceParameters(deviceFieldBar);
      }
    } else {
      const deviceField = form.getFieldValue(["locations", 0, "device"]);
      if (deviceField && deviceField.length > 0) {
        getDeviceParameters(deviceField);
      }
    }
  }, [form]);

  useEffect(() => {
    if (component === "alarmNotification") {
      if (businessList?.length === 1) {
        const singleBusinessId = businessList[0]._id;
        if (form.getFieldValue("business") !== singleBusinessId) {
          form.setFieldValue("business", singleBusinessId);
        }
        setEditItem((pre) => (pre?.business !== singleBusinessId ? { ...pre, business: singleBusinessId } : pre));
        getSiteLocations(singleBusinessId);
      } else {
        const currentBusiness = form.getFieldValue("business") || editItem?.business;
        if (currentBusiness) {
          getSiteLocations(currentBusiness);
        }
      }

      // Sync form fields from editItem if available
      if (editItem) {
        const syncValues = {};
        if (editItem.business && form.getFieldValue("business") !== editItem.business) {
          syncValues.business = editItem.business;
        }
        if (editItem.site !== undefined && JSON.stringify(form.getFieldValue("site")) !== JSON.stringify(editItem.site)) {
          syncValues.site = editItem.site;
        }
        if (editItem.notificationTypes !== undefined && JSON.stringify(form.getFieldValue("notificationTypes")) !== JSON.stringify(editItem.notificationTypes)) {
          syncValues.notificationTypes = editItem.notificationTypes;
        }
        if (editItem.notificationLimit !== undefined && form.getFieldValue("notificationLimit") !== editItem.notificationLimit) {
          syncValues.notificationLimit = editItem.notificationLimit;
        }
        if (Object.keys(syncValues).length > 0) {
          form.setFieldsValue(syncValues);
        }
      }
    }
  }, [businessList, component, editItem]);

  return (
    <>
      {component === "alarmNotification" ? (
        <>
          <Form.Item
            name="business"
            label="Business"
            rules={[{ required: true, message: "Please select business" }]}
          >
            <Select
              placeholder="Select Business"
              allowClear={businessList?.length !== 1}
              disabled={businessList?.length === 1}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={
                Array.isArray(businessList)
                  ? businessList.map((item) => ({ label: `${item.name} (${item.id})`, value: item._id }))
                  : []
              }
              onChange={(val) => {
                form.setFieldValue("site", undefined);
                setEditItem((pre) => ({ ...pre, business: val, site: undefined }));
                if (val) {
                  getSiteLocations(val);
                } else {
                  setSiteLocationList([]);
                }
              }}
            />
          </Form.Item>

          <Form.Item name="site" label="Site Location">
            <Select
              style={{ width: "100%" }}
              mode="multiple"
              showSearch
              allowClear
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              maxTagCount="responsive"
              placeholder="Select Site Location"
              loading={isSiteLoading}
              options={
                Array.isArray(siteLocationList)
                  ? siteLocationList.map((item) => ({ label: item.name, value: item._id }))
                  : []
              }
              onChange={(values) => {
                setEditItem((pre) => ({ ...pre, site: values }));
              }}
            />
          </Form.Item>

          <Form.Item
            name="notificationTypes"
            label="Notification Type"
            rules={[{ required: true, message: "Please select at least one notification type" }]}
          >
            <Checkbox.Group
              options={[
                { label: "Device Offline", value: "offlineDevice" },
                { label: "Alarm Triggered", value: "alarm" },
              ]}
              onChange={(values) => {
                setEditItem((pre) => ({ ...pre, notificationTypes: values }));
              }}
            />
          </Form.Item>

          <Form.Item
            name="notificationLimit"
            label="No of Notifications"
            rules={[{ required: true, message: "Please select display total" }]}
          >
            <Select
              placeholder="Select Display Total"
              options={[
                { label: "1", value: 1 },
                { label: "2", value: 2 },
                { label: "3", value: 3 },
                { label: "4", value: 4 },
                { label: "5", value: 5 },
                { label: "6", value: 6 },
                { label: "7", value: 7 },
                { label: "8", value: 8 },
                { label: "9", value: 9 },
                { label: "10", value: 10 },
              ]}
              onChange={(val) => {
                setEditItem((pre) => ({ ...pre, notificationLimit: val }));
              }}
            />
          </Form.Item>
        </>
      ) : ["counter", "splitCounter"].includes(component) && counterDataType === "custom" ? (
        <>
          <Form.Item name="counterValue" label="Select Counter Value">
            <InputNumber className="w-100" placeholder="Enter Custom Value" />
          </Form.Item>
          <Form.Item name="counterUnit" label="Select Counter Unit">
            <Input className="w-100" placeholder="Enter Custom Unit" />
          </Form.Item>
        </>
      ) : ["comboBar"].includes(component) ? (
        <>
          <div>
            <div className="mb-2 fw-bold"> Line Chart: </div>
            <Form.List name={["comboLocations", "line"]} initialValue={[{}]}>
              {(fields) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key}>
                      {/* {console.log({ key, name, restField })} */}
                      <Form.Item
                        {...restField}
                        name={[name, "business"]}
                        label="Business"
                        rules={[{ required: true, message: "Select Business" }]}
                      >
                        <Select
                          style={{ width: "100%" }}
                          mode={userDetails?.role === userRole.superAdmin ? "multiple" : ""}
                          showSearch
                          filterOption={(input, option) =>
                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                          }
                          maxTagCount="responsive"
                          placeholder="Select Business"
                          onChange={(values) => {
                            setSiteLocationList([]);
                            setDeviceList([]);
                            setParameterList([]);
                            getSiteLocations(values);
                            const comboLocations = {
                              line: [{ business: values, site: undefined, device: undefined, yAxis: undefined }],
                            };
                            setEditItem((pre) => ({ ...pre, comboLocations }));
                            form.setFieldsValue({ comboLocations });
                          }}
                          disabled={businessList?.length === 1}
                          options={
                            Array.isArray(businessList)
                              ? businessList?.map((item) => ({ label: `${item.name} (${item.id})`, value: item._id }))
                              : []
                          }
                        />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, "site"]}
                        label="Site Location"
                        rules={[{ required: true, message: "Select Site Location" }]}
                      >
                        <Select
                          style={{ width: "100%" }}
                          mode="multiple"
                          // mode={
                          //   [userRole.superAdmin, userRole.businessAdmin].includes(userDetails?.role) ? "multiple" : ""
                          // }
                          showSearch
                          filterOption={(input, option) =>
                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                          }
                          maxTagCount="responsive"
                          placeholder="Select Site Location"
                          onChange={(values) => {
                            setDeviceList([]);
                            setParameterList([]);
                            getDevice(values);
                            setEditItem((pre) => ({
                              ...pre,
                              comboLocations: {
                                line: [
                                  { ...pre.comboLocations?.line, site: values, device: undefined, yAxis: undefined },
                                ],
                              },
                            }));
                            form.setFieldsValue({
                              comboLocations: {
                                line: [
                                  {
                                    ...form.getFieldValue(["comboLocations", "line", 0]),
                                    site: values,
                                    device: undefined,
                                    yAxis: undefined,
                                  },
                                ],
                              },
                            });
                          }}
                          loading={isSiteLoading}
                          options={
                            Array.isArray(siteLocationList)
                              ? siteLocationList?.map((item) => ({ label: item.name, value: item._id }))
                              : []
                          }
                        />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, "device"]}
                        label="Device"
                        rules={[{ required: true, message: "Select Device" }]}
                      >
                        <Select
                          style={{ width: "100%" }}
                          // mode={component !== "dynamicBox" ? "multiple" : null}
                          mode={
                            component &&
                            !["dynamicBox", "tank", "counter", "splitCounter", "dial", "thermometer"].includes(
                              component,
                            )
                              ? "multiple"
                              : null
                          }
                          showSearch
                          filterOption={(input, option) =>
                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                          }
                          maxTagCount="responsive"
                          placeholder="Select Device"
                          loading={isDeviceLoading}
                          onChange={(values) => {
                            setParameterList([]);
                            getDeviceParameters(values);
                            setEditItem((pre) => ({
                              ...pre,
                              comboLocations: {
                                line: [{ ...pre.comboLocations?.line, device: values, yAxis: undefined }],
                              },
                            }));
                            form.setFieldsValue({
                              comboLocations: {
                                line: [
                                  {
                                    ...form.getFieldValue(["comboLocations", "line", 0]),
                                    device: values,
                                    yAxis: undefined,
                                  },
                                ],
                              },
                            });
                          }}
                          options={
                            Array.isArray(deviceList)
                              ? deviceList?.map((item) => ({ label: item.name, value: item._id }))
                              : []
                          }
                        />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, "yAxis"]}
                        label="Parameter"
                        rules={[{ required: true, message: "Select Parameter" }]}
                      >
                        <Select
                          style={{ width: "100%" }}
                          mode={
                            component &&
                            !["dynamicBox", "tank", "counter", "splitCounter", "dial", "thermometer"].includes(
                              component,
                            )
                              ? "multiple"
                              : null
                          }
                          showSearch
                          filterOption={(input, option) =>
                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                          }
                          maxTagCount="responsive"
                          placeholder="Select Parameter"
                          loading={isParameterLoading}
                          options={
                            Array.isArray(parameterList)
                              ? parameterList?.map((item) => ({
                                  label: `${item?.device?.site?.name} > ${item?.device?.name} > ${item.name}`,
                                  value: item._id,
                                }))
                              : []
                          }
                        />
                      </Form.Item>
                    </div>
                  ))}
                </>
              )}
            </Form.List>
          </div>

          <div className="mt-5">
            <div className="mb-2 fw-bold"> Bar Chart: </div>
            <Form.List name={["comboLocations", "bar"]} initialValue={[{}]}>
              {(fields) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key}>
                      {/* {console.log({ key, name, restField })} */}
                      <Form.Item
                        {...restField}
                        name={[name, "business"]}
                        label="Business"
                        rules={[{ required: true, message: "Select Business" }]}
                      >
                        <Select
                          style={{ width: "100%" }}
                          mode={userDetails?.role === userRole.superAdmin ? "multiple" : ""}
                          showSearch
                          filterOption={(input, option) =>
                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                          }
                          maxTagCount="responsive"
                          placeholder="Select Business"
                          onChange={(values) => {
                            setSiteLocationList([]);
                            setDeviceList([]);
                            setParameterList([]);
                            getSiteLocations(values);
                            const comboLocations = {
                              bar: [{ business: values, site: undefined, device: undefined, yAxis: undefined }],
                            };
                            setEditItem((pre) => ({ ...pre, comboLocations }));
                            form.setFieldsValue({ comboLocations });
                          }}
                          disabled={businessList?.length === 1}
                          options={
                            Array.isArray(businessList)
                              ? businessList?.map((item) => ({ label: `${item.name} (${item.id})`, value: item._id }))
                              : []
                          }
                        />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, "site"]}
                        label="Site Location"
                        rules={[{ required: true, message: "Select Site Location" }]}
                      >
                        <Select
                          style={{ width: "100%" }}
                          mode="multiple"
                          showSearch
                          filterOption={(input, option) =>
                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                          }
                          maxTagCount="responsive"
                          placeholder="Select Site Location"
                          onChange={(values) => {
                            setDeviceList([]);
                            setParameterList([]);
                            getDevice(values);
                            setEditItem((pre) => ({
                              ...pre,
                              comboLocations: {
                                bar: [
                                  { ...pre.comboLocations?.bar, site: values, device: undefined, yAxis: undefined },
                                ],
                              },
                            }));
                            form.setFieldsValue({
                              comboLocations: {
                                bar: [
                                  {
                                    ...form.getFieldValue(["comboLocations", "bar", 0]),
                                    site: values,
                                    device: undefined,
                                    yAxis: undefined,
                                  },
                                ],
                              },
                            });
                          }}
                          loading={isSiteLoading}
                          options={
                            Array.isArray(siteLocationList)
                              ? siteLocationList?.map((item) => ({ label: item.name, value: item._id }))
                              : []
                          }
                        />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, "device"]}
                        label="Device"
                        rules={[{ required: true, message: "Select Device" }]}
                      >
                        <Select
                          style={{ width: "100%" }}
                          // mode={component !== "dynamicBox" ? "multiple" : null}
                          mode={
                            component &&
                            !["dynamicBox", "tank", "counter", "splitCounter", "dial", "thermometer"].includes(
                              component,
                            )
                              ? "multiple"
                              : null
                          }
                          showSearch
                          filterOption={(input, option) =>
                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                          }
                          maxTagCount="responsive"
                          placeholder="Select Device"
                          loading={isDeviceLoading}
                          onChange={(values) => {
                            setParameterList([]);
                            getDeviceParameters(values);
                            setEditItem((pre) => ({
                              ...pre,
                              comboLocations: {
                                bar: [{ ...pre.comboLocations?.bar, device: values, yAxis: undefined }],
                              },
                            }));
                            form.setFieldsValue({
                              comboLocations: {
                                bar: [
                                  {
                                    ...form.getFieldValue(["comboLocations", "bar", 0]),
                                    device: values,
                                    yAxis: undefined,
                                  },
                                ],
                              },
                            });
                          }}
                          options={
                            Array.isArray(deviceList)
                              ? deviceList?.map((item) => ({ label: item.name, value: item._id }))
                              : []
                          }
                        />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, "yAxis"]}
                        label="Parameter"
                        rules={[{ required: true, message: "Select Parameter" }]}
                      >
                        <Select
                          style={{ width: "100%" }}
                          mode={
                            component &&
                            !["dynamicBox", "tank", "counter", "splitCounter", "dial", "thermometer"].includes(
                              component,
                            )
                              ? "multiple"
                              : null
                          }
                          showSearch
                          filterOption={(input, option) =>
                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                          }
                          maxTagCount="responsive"
                          placeholder="Select Parameter"
                          loading={isParameterLoading}
                          options={
                            Array.isArray(parameterList)
                              ? parameterList?.map((item) => ({
                                  label: `${item?.device?.site?.name} > ${item?.device?.name} > ${item.name}`,
                                  value: item._id,
                                }))
                              : []
                          }
                        />
                      </Form.Item>
                    </div>
                  ))}
                </>
              )}
            </Form.List>
          </div>
        </>
      ) : (
        <Form.List name="locations" initialValue={[{}]}>
          {(fields) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <div key={key}>
                  <Form.Item
                    {...restField}
                    name={[name, "business"]}
                    label="Business"
                    rules={[{ required: true, message: "Select Business" }]}
                  >
                    <Select
                      style={{ width: "100%" }}
                      mode={userDetails?.role === userRole.superAdmin ? "multiple" : ""}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                      maxTagCount="responsive"
                      placeholder="Select Business"
                      onChange={(values) => {
                        setSiteLocationList([]);
                        setDeviceList([]);
                        setParameterList([]);
                        getSiteLocations(values);
                        const locations = [{ business: values, site: undefined, device: undefined, yAxis: undefined }];
                        setEditItem((pre) => ({ ...pre, locations }));
                        form.setFieldsValue({ locations });
                      }}
                      disabled={businessList?.length === 1}
                      options={
                        Array.isArray(businessList)
                          ? businessList?.map((item) => ({ label: `${item.name} (${item.id})`, value: item._id }))
                          : []
                      }
                    />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, "site"]}
                    label="Site Location"
                    rules={[{ required: true, message: "Select Site Location" }]}
                  >
                    <Select
                      style={{ width: "100%" }}
                      // mode={[userRole.superAdmin, userRole.businessAdmin].includes(userDetails?.role) ? "multiple" : ""}
                      mode="multiple"
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                      maxTagCount="responsive"
                      placeholder="Select Site Location"
                      onChange={(values) => {
                        setDeviceList([]);
                        setParameterList([]);
                        getDevice(values);
                        setEditItem((pre) => ({
                          ...pre,
                          locations: [{ ...pre.locations, site: values, device: undefined, yAxis: undefined }],
                        }));
                        form.setFieldsValue({
                          locations: [
                            {
                              ...form.getFieldValue(["locations", 0]),
                              site: values,
                              device: undefined,
                              yAxis: undefined,
                            },
                          ],
                        });
                      }}
                      loading={isSiteLoading}
                      options={
                        Array.isArray(siteLocationList)
                          ? siteLocationList?.map((item) => ({ label: item.name, value: item._id }))
                          : []
                      }
                    />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, "device"]}
                    label="Device"
                    rules={[{ required: true, message: "Select Device" }]}
                  >
                    <Select
                      style={{ width: "100%" }}
                      // mode={component !== "dynamicBox" ? "multiple" : null}
                      mode={
                        component &&
                        !["dynamicBox", "loadSummary", "tank", "counter", "splitCounter", "dial", "thermometer"].includes(component)
                          ? "multiple"
                          : null
                      }
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                      maxTagCount="responsive"
                      placeholder="Select Device"
                      loading={isDeviceLoading}
                      onChange={(values) => {
                        setParameterList([]);
                        getDeviceParameters(values);
                        setEditItem((pre) => ({
                          ...pre,
                          locations: [{ ...pre.locations, device: values, yAxis: undefined }],
                        }));
                        form.setFieldsValue({
                          locations: [
                            {
                              ...form.getFieldValue(["locations", 0]),
                              device: values,
                              yAxis: undefined,
                            },
                          ],
                        });
                      }}
                      options={
                        Array.isArray(deviceList)
                          ? deviceList?.map((item) => ({ label: item.name, value: item._id }))
                          : []
                      }
                    />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, "yAxis"]}
                    label="Parameter"
                    rules={[{ required: true, message: "Select Parameter" }]}
                  >
                    <Select
                      style={{ width: "100%" }}
                      mode={
                        component &&
                        !["dynamicBox", "loadSummary", "tank", "counter", "splitCounter", "dial", "thermometer"].includes(component)
                          ? "multiple"
                          : null
                      }
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                      maxTagCount="responsive"
                      placeholder="Select Parameter"
                      loading={isParameterLoading}
                      options={
                        Array.isArray(parameterList)
                          ? parameterList?.map((item) => ({
                              label: `${item?.device?.site?.name} > ${item?.device?.name} > ${item.name}`,
                              value: item._id,
                            }))
                          : []
                      }
                    />
                  </Form.Item>
                </div>
              ))}
            </>
          )}
        </Form.List>
      )}

      {["dial"].includes(component) && (
        <Form.Item name="unit" label="Enter Unit">
          <Input className="w-100" placeholder="Enter Unit" />
        </Form.Item>
      )}
    </>
  );
}

export default DataTab;

DataTab.propTypes = tabProps;
